import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PricingDetail, InstantEpriceContainer } from 'wsm-pricing-display';
import { useDealerOptions } from 'wsm-pricing-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { ButtonList } from 'wsm-vehicle-cta-display';
import { Tabs, Tab } from 'react-bootstrap';
import { useRequestData, usePrefs, useLabels } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { DetailedIncentives, filterIncentiveIds } from 'wsm-incentive-display';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import logger from 'ws-scripts/modules/logger';
import VehicleAttributeList from './VehicleAttributeList';
import Callout from './Callout';
import VehiclePackages from './VehiclePackages';
import { getVehicleObj } from '../utilities/vehicleTracking';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../utilities/getTargetLocations';

const VehicleCardData = ({
	accountId,
	accountInfo,
	incentives,
	isGridLayout,
	isTabbedLayout,
	vehicle
}) => {
	const {
		buttonSizeEPrice,
		buttonStyleEPrice,
		ePriceFontWeight,
		incentiveDisplayGrouping,
		incentiveTitleCustom,
		incentiveTitleDisplay,
		instantEpricePreventMultipleEpriceLeads,
		instantEpriceShowAllAfterLeadSubmit,
		restrictInsertLocationsForVehicles,
		showDealerConditionalIncentives,
		showDealerRegularIncentives,
		showIncentives,
		showInstantEpriceForSharedInventory,
		showOemConditionalIncentives,
		showOemNationalIncentives,
		showPackages,
		tabInfoLabel,
		tabOrder,
		tabPricingLabel,
		tabSpecialsLabel,
		hideInformationTabs
	} = usePrefs();

	const {
		accountId: vehicleAccountId,
		attributes = [],
		bestIncentiveIds = [],
		callout: callouts,
		condition,
		incentiveIds = [],
		inventoryButtons = [],
		link,
		packages = [],
		pricing = {},
		uuid
	} = vehicle;

	// TODO: remove mapping here (and in ws-detailed-pricing) and update wsm-pricing-display to use lowercase version once GVM databus is retired
	const { dprice, dPrice, eprice, ePrice, ...restPricing } = pricing;

	const componentPricing = {
		dPrice: dPrice || dprice,
		ePrice: ePrice || eprice,
		...restPricing
	};

	const incentiveIdList = incentiveIds || bestIncentiveIds || [];

	const [shouldRequestDealerOptions, setShouldRequestDealerOptions] =
		useState(false);
	const [isDisplayingEprice, setIsDisplayingEPrice] = useState(false);
	const { userProfile } = useSelector((state) => state);
	const { pageAlias } = useSelector((state) => state.widgetInfo);
	const { widgetName, windowId, locale } = useRequestData();
	const labels = useLabels();
	const dealerOptions = useDealerOptions(
		uuid,
		locale,
		shouldRequestDealerOptions,
		widgetName
	);

	const tabArray = [];
	const orderedTabs = tabOrder.replace(/\s/g, '').toLowerCase().split(',');
	const tabLabels = {
		info: labels.get(tabInfoLabel),
		specials: labels.get(tabSpecialsLabel),
		pricing: labels.get(tabPricingLabel)
	};

	const handleDealerOptionsMouseEnter = () => {
		setShouldRequestDealerOptions(true);
	};

	const showEPrice = (id) => {
		setIsDisplayingEPrice(id === uuid && id !== 'none');
	};

	const ePriceButton = !isDisplayingEprice ? (
		<InstantEpriceContainer
			accountId={accountId}
			buttonSize={buttonSizeEPrice}
			buttonStyle={buttonStyleEPrice}
			ePriceFontWeight={ePriceFontWeight}
			instantEpricePreventMultipleEpriceLeads={
				instantEpricePreventMultipleEpriceLeads
			}
			instantEpriceShowAllAfterLeadSubmit={
				instantEpriceShowAllAfterLeadSubmit
			}
			key="eprice"
			pageAlias={pageAlias}
			pricing={componentPricing}
			showEPrice={(id) => showEPrice(id)}
			showInstantEpriceForSharedInventory={
				showInstantEpriceForSharedInventory
			}
			userProfile={userProfile}
			vehicleId={uuid}
			vehicleAccountId={vehicleAccountId}
		/>
	) : null;

	const filteredIncentives = filterIncentiveIds(incentives, incentiveIdList);
	const vehicleTrackingData = getVehicleObj(uuid);

	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);
	const vehiclePaymentsLocation = getVehicleLocationName(
		'vehicle-payments',
		targetLocations,
		restrictInsertLocationsForVehicles
	);
	const vehiclePricingLocation = getVehicleLocationName(
		'vehicle-pricing',
		targetLocations,
		restrictInsertLocationsForVehicles
	);
	const vehicleCtasLocation = getVehicleLocationName(
		'vehicle-ctas',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const tabComponents = {
		info: (
			<React.Fragment>
				<VehicleAttributeList attributes={attributes} />
				{isTabbedLayout && (
					<React.Fragment>
						{showPackages === 'true' && (
							<VehiclePackages packages={packages} link={link} />
						)}
						<Callout
							callouts={callouts}
							vehicle={vehicle}
							isTabbedLayout={isTabbedLayout}
						/>
					</React.Fragment>
				)}
			</React.Fragment>
		),
		specials:
			showIncentives === 'true' && filteredIncentives.length ? (
				<DetailedIncentives
					incentives={filteredIncentives}
					accountInfo={accountInfo}
					incentiveTitleDisplay={incentiveTitleDisplay}
					incentiveTitleCustom={incentiveTitleCustom}
					incentiveDisplayGrouping={incentiveDisplayGrouping}
					enableAccordion={!isTabbedLayout}
					showDealerRegularIncentives={
						showDealerRegularIncentives === 'true'
					}
					showDealerConditionalIncentives={
						showDealerConditionalIncentives === 'true'
					}
					showOemNationalIncentives={
						showOemNationalIncentives === 'true'
					}
					showOemConditionalIncentives={
						showOemConditionalIncentives === 'true'
					}
				/>
			) : null,
		pricing: (
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-listing error boundary: wsm-pricing-display error.\n${error}`
					);
					const newError = new Error(
						`ws-inv-listing error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
				}}
			>
				<PricingDetail
					{...{
						pricing: componentPricing,
						incentives,
						accountInfo,
						accountId,
						condition,
						isGridLayout,
						userProfile
					}}
					fetchDealerOptionsCallback={handleDealerOptionsMouseEnter}
					dealerOptions={dealerOptions}
					vehicleAccountId={vehicleAccountId}
					vehicleId={uuid}
					isDisplayingEprice={isDisplayingEprice}
				/>
				<div
					className="hidden vehicle-card-payments mt-3"
					data-location={vehiclePaymentsLocation}
				/>
			</ErrorBoundary>
		)
	};

	const hasInventoryButtons = inventoryButtons && inventoryButtons.length > 0;

	// prettier is causing the linter to error. I really do not care about these indents
	/* eslint-disable react/jsx-indent */
	const ctaButtons = targetLocations.has('vehicle-ctas')
		? [
				<ButtonList
					buttons={[]}
					key="inventoryButtons"
					vehicleTrackingData={undefined}
					dataLocation={vehicleCtasLocation}
				/>
		  ]
		: [
				ePriceButton,
				<ButtonList
					buttons={inventoryButtons}
					key="inventoryButtons"
					vehicleTrackingData={
						hasInventoryButtons ? vehicleTrackingData : undefined
					}
					dataLocation={vehicleCtasLocation}
				/>
		  ];
	/* eslint-enable react/jsx-indent */

	orderedTabs.map(
		(key) =>
			tabComponents[key] &&
			tabArray.push({
				eventKey: key,
				key,
				title: tabLabels[key],
				component: tabComponents[key]
			})
	);

	return (
		<React.Fragment>
			{isTabbedLayout ? (
				<React.Fragment>
					<Tabs
						className="vehicle-card-tabs tabs"
						id={uuid}
						onSelect={(key) =>
							trackEvent(widgetName, windowId, {
								element: `${tabLabels[key]} tab`,
								result: 'Tab displayed'
							})
						}
					>
						{tabArray
							.filter((tab) => {
								return !hideInformationTabs
									?.toLowerCase()
									.includes(tab.key);
							})
							.map((tab) => (
								<Tab
									eventKey={tab.eventKey}
									key={tab.key}
									title={tab.title}
									tabClassName="w-100 text-center"
								>
									{tab.component}
								</Tab>
							))}
					</Tabs>
					{/* to make sure VehicleTitle always displays pricing */}
					{hideInformationTabs?.toLowerCase().includes('pricing') && (
						<div className="d-none">{tabComponents.pricing}</div>
					)}
					<ErrorBoundary
						errorHandler={(error, errorInfo) => {
							setNewRelicCustomAttribute(
								'SRP ERROR',
								`ws-inv-listing error boundary: cta buttons error.\n${error}`
							);
							const newError = new Error(
								`ws-inv-listing error boundary.\n${error}`
							);
							newError.originalError = error;
							newError.originalStackTrace =
								errorInfo.componentStack;
							logger.error(
								`${newError}\n${newError.originalStackTrace}`
							);
						}}
					>
						{ctaButtons}
					</ErrorBoundary>
					<div
						className="hidden vehicle-card-pricing mt-3"
						data-location={vehiclePricingLocation}
					/>
				</React.Fragment>
			) : (
				<div className="d-md-flex">
					<div className="vehicle-card-info-packages w-100">
						{tabComponents.info}
						{showPackages === 'true' && (
							<VehiclePackages packages={packages} link={link} />
						)}
					</div>
					<div className="vehicle-card-pricing w-100">
						<div className="w-100">{tabComponents.pricing}</div>
						<div className="w-100">{tabComponents.specials}</div>
						<div className="w-100">{ctaButtons}</div>
						<div className="w-100">
							<div
								className="hidden vehicle-card-pricing mt-3"
								data-location={vehiclePricingLocation}
							/>
						</div>
					</div>
				</div>
			)}
		</React.Fragment>
	);
};

VehicleCardData.propTypes = {
	accountId: PropTypes.string.isRequired,
	accountInfo: PropTypes.shape({}),
	accounts: PropTypes.shape({}),
	incentives: PropTypes.shape({}),
	isGridLayout: PropTypes.bool,
	isTabbedLayout: PropTypes.bool.isRequired,
	vehicle: PropTypes.shape({
		accountId: PropTypes.string,
		attributes: PropTypes.arrayOf(PropTypes.shape({})),
		bestIncentiveIds: PropTypes.arrayOf(PropTypes.string),
		callout: PropTypes.arrayOf(PropTypes.shape({})),
		condition: PropTypes.string,
		incentiveIds: PropTypes.arrayOf(PropTypes.string),
		inventoryButtons: PropTypes.arrayOf(PropTypes.shape({})),
		link: PropTypes.string,
		packages: PropTypes.arrayOf(PropTypes.string),
		pricing: PropTypes.shape({
			dprice: PropTypes.arrayOf(PropTypes.shape({})),
			dPrice: PropTypes.arrayOf(PropTypes.shape({})),
			eprice: PropTypes.arrayOf(PropTypes.shape({})),
			ePrice: PropTypes.arrayOf(PropTypes.shape({}))
		}),
		title: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string)
		]),
		uuid: PropTypes.string
	}).isRequired
};

VehicleCardData.defaultProps = {
	accounts: {},
	incentives: {}
};

export default VehicleCardData;
